<template>
  <section
    class="py-16 bg-gradient-to-b from-gray-100 to-white overflow-hidden"
  >
    <div class="max-w-7xl mx-auto px-4">
      <!-- Section Header -->
      <!-- <div class="text-center mb-8 md:mb-12">
        <h2 class="text-4xl font-bold mb-4">
          Latest
          <span
            class="text-primary hover:animate-bounce inline-block cursor-pointer"
            >Buzz</span
          >
        </h2>
        <p class="text-gray-600 max-w-2xl mx-auto">
          Stay in the loop with our latest updates and insights from the
          academic world.
        </p>
      </div> -->

      <!-- Main Content Area (Desktop: Left/Right, Mobile: Stacked) -->
      <div class="flex flex-col md:flex-row md:gap-12">
        <!-- Left Column (Desktop Only - Navigation & Info) -->
        <div
          class="hidden md:block md:w-1/3 md:sticky md:top-24 md:self-start space-y-8"
        >
          <h2 class="text-4xl font-bold mb-4">
            Latest
            <span
              class="text-primary hover:animate-bounce inline-block cursor-pointer"
              >Buzz</span
            >
          </h2>

          <p class="text-gray-600 text-sm">
            Explore recent articles and news. Use the arrows to navigate through
            posts.
          </p>
          <!-- Desktop Navigation -->
          <div class="flex items-center justify-start gap-4">
            <button
              @click="scrollPrev"
              :class="[
                'p-3 rounded-full bg-white shadow-md hover:shadow-lg transition-all duration-300 group flex items-center justify-center w-12 h-12',
                { 'opacity-50 cursor-not-allowed': isAtStart },
              ]"
              :disabled="isAtStart"
              aria-label="Previous Post"
            >
              <Icon
                name="heroicons:arrow-left"
                class="w-5 h-5 text-primary group-hover:-translate-x-1 transition-transform"
              />
            </button>

            <div class="flex space-x-1.5">
              <button
                v-for="(_, index) in totalPages"
                :key="`dot-${index}`"
                @click="goToPage(index)"
                :aria-label="`Go to page ${index + 1}`"
                class="w-2 h-2 rounded-full transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                :class="
                  currentPage === index
                    ? 'bg-primary w-6'
                    : 'bg-gray-300 hover:bg-gray-400'
                "
              ></button>
            </div>

            <button
              @click="scrollNext"
              :class="[
                'p-3 rounded-full bg-white shadow-md hover:shadow-lg transition-all duration-300 group flex items-center justify-center w-12 h-12',
                { 'opacity-50 cursor-not-allowed': isAtEnd },
              ]"
              :disabled="isAtEnd"
              aria-label="Next Post"
            >
              <Icon
                name="heroicons:arrow-right"
                class="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform"
              />
            </button>
          </div>
          <!-- Desktop View All Button -->
          <UButton
            to="/buzz?utm_source=cardHome"
            text="View All Posts"
            class="px-6 py-2.5 font-medium w-full md:w-auto"
            variant="outline"
            color="primary"
            size="lg"
          >
            <template #trailing>
              <Icon name="heroicons:arrow-right" class="w-5 h-5 ml-2" />
            </template>
          </UButton>
        </div>

        <!-- Right Column (Desktop) / Main Content (Mobile) - Posts Carousel -->
        <div class="w-full md:w-2/3 relative">
          <!-- Carousel Container -->
          <div
            class="overflow-hidden relative"
            ref="postsContainer"
            @mouseenter="pauseAutoScroll"
            @mouseleave="resumeAutoScroll"
            @touchstart="pauseAutoScroll"
            @touchend="resumeAutoScroll"
          >
            <!-- Loading State -->
            <div v-if="isLoading" class="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div
                v-for="i in postsPerPage"
                :key="`loading-${i}`"
                class="bg-white p-6 rounded-xl shadow-sm animate-pulse"
              >
                <div class="h-40 bg-gray-200 rounded-lg mb-4"></div>
                <div class="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div class="h-4 bg-gray-200 rounded w-full mb-2"></div>
                <div class="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>

            <!-- Posts Grid -->
            <div
              v-else-if="featuredPosts.length > 0"
              class="flex transition-transform duration-500 ease-out"
              :style="{ transform: `translateX(-${currentPage * 100}%)` }"
            >
              <div
                v-for="(chunk, i) in postChunks"
                :key="`chunk-${i}`"
                class="w-full flex-shrink-0 grid grid-cols-1 sm:grid-cols-2 gap-6"
              >
                <BuzzCard
                  v-for="post in chunk"
                  :key="post.id"
                  :post="post"
                  class="h-full"
                />
              </div>
            </div>
            <!-- No Posts State -->
            <div v-else class="text-center py-12 text-gray-500">
              No posts available at the moment. Check back soon!
            </div>
          </div>

          <!-- Mobile Navigation -->
          <div class="md:hidden flex items-center justify-center gap-4 mt-8">
            <button
              @click="scrollPrev"
              :class="[
                'p-3 rounded-full bg-white shadow-md hover:shadow-lg active:shadow-sm transition-all duration-300 group flex items-center justify-center w-12 h-12',
                { 'opacity-50 cursor-not-allowed': isAtStart },
              ]"
              :disabled="isAtStart"
              aria-label="Previous Post"
            >
              <Icon
                name="heroicons:arrow-left-solid"
                class="w-5 h-5 text-primary"
              />
            </button>

            <div class="flex space-x-1.5">
              <button
                v-for="(_, index) in totalPages"
                :key="`mobile-dot-${index}`"
                @click="goToPage(index)"
                :aria-label="`Go to page ${index + 1}`"
                class="w-2 h-2 rounded-full transition-all duration-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                :class="
                  currentPage === index ? 'bg-primary w-6' : 'bg-gray-300'
                "
              ></button>
            </div>

            <button
              @click="scrollNext"
              :class="[
                'p-3 rounded-full bg-white shadow-md hover:shadow-lg active:shadow-sm transition-all duration-300 group flex items-center justify-center w-12 h-12',
                { 'opacity-50 cursor-not-allowed': isAtEnd },
              ]"
              :disabled="isAtEnd"
              aria-label="Next Post"
            >
              <Icon
                name="heroicons:arrow-right-solid"
                class="w-5 h-5 text-primary"
              />
            </button>
          </div>

          <!-- Mobile View All Button -->
          <div class="mt-8 text-center md:hidden">
            <UButton
              to="/buzz?utm_source=cardHome"
              text="View All Posts"
              class="px-6 py-2.5 font-medium"
              variant="primary"
              size="lg"
            >
              <template #trailing>
                <Icon name="heroicons:arrow-right" class="w-5 h-5 ml-2" />
              </template>
            </UButton>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useBuzzStore } from "~/stores/buzz";
import { useSwipe, useWindowSize } from "@vueuse/core";
import { storeToRefs } from "pinia";

const buzzStore = useBuzzStore();
const { isLoading, error } = storeToRefs(buzzStore);
const postsContainer = ref<HTMLElement | null>(null);
const currentPage = ref(0);
const autoScrollInterval = ref<ReturnType<typeof setInterval> | null>(null);
const autoScrollDelay = 5000; // 5 seconds
const isHovering = ref(false);

// --- Responsive Logic ---
const { width } = useWindowSize();
const isMobile = computed(() => width.value < 768); // Tailwind's md breakpoint

// Adjust posts per page based on screen size
const postsPerPage = computed(() => (isMobile.value ? 1 : 2));

// --- Data Fetching & Processing ---
onMounted(async () => {
  // Fetch enough posts to fill a few pages potentially
  await buzzStore.fetchAllPosts({ pageSize: 12, sort: "publishedAt:desc" });
  startAutoScroll();
});

onUnmounted(() => {
  stopAutoScroll();
});

const featuredPosts = computed(() => {
  // Use the getter which already sorts and limits
  return buzzStore.getAllPosts.slice(0, 12); // Limit to 12 for this component
});

const postChunks = computed(() => {
  const chunks = [];
  const posts = featuredPosts.value;
  const perPage = postsPerPage.value;
  for (let i = 0; i < posts.length; i += perPage) {
    chunks.push(posts.slice(i, i + perPage));
  }
  // Ensure the last chunk has enough items for the grid layout if needed (less critical if using flex)
  return chunks;
});

const totalPages = computed(() => postChunks.value.length);

// --- Navigation Logic ---
const isAtStart = computed(() => currentPage.value === 0);
const isAtEnd = computed(() => currentPage.value >= totalPages.value - 1);

function goToPage(index: number) {
  if (index >= 0 && index < totalPages.value) {
    currentPage.value = index;
    resetAutoScroll();
  }
}

function scrollNext() {
  if (!isAtEnd.value) {
    currentPage.value++;
  } else {
    currentPage.value = 0; // Loop back to start
  }
  resetAutoScroll();
}

function scrollPrev() {
  if (!isAtStart.value) {
    currentPage.value--;
  } else {
    currentPage.value = totalPages.value - 1; // Loop back to end
  }
  resetAutoScroll();
}

// --- Auto-scroll ---
function startAutoScroll() {
  stopAutoScroll(); // Clear any existing interval
  if (totalPages.value > 1 && !isHovering.value) {
    autoScrollInterval.value = setInterval(() => {
      scrollNext();
    }, autoScrollDelay);
  }
}

function stopAutoScroll() {
  if (autoScrollInterval.value) {
    clearInterval(autoScrollInterval.value);
    autoScrollInterval.value = null;
  }
}

function resetAutoScroll() {
  stopAutoScroll();
  startAutoScroll();
}

function pauseAutoScroll() {
  isHovering.value = true;
  stopAutoScroll();
}

function resumeAutoScroll() {
  isHovering.value = false;
  startAutoScroll();
}

// --- Swipe Gestures ---
useSwipe(postsContainer, {
  passive: true, // Improve scroll performance
  onSwipeEnd(e, direction) {
    if (direction === "left") {
      scrollNext();
    } else if (direction === "right") {
      scrollPrev();
    }
  },
});

// Watch for changes that might affect pagination or auto-scroll
watch(totalPages, (newTotal) => {
  if (currentPage.value >= newTotal) {
    currentPage.value = Math.max(0, newTotal - 1);
  }
  resetAutoScroll(); // Restart or stop based on new total pages
});

watch(isMobile, () => {
  // Reset current page if layout changes drastically? Optional.
  // currentPage.value = 0;
  resetAutoScroll();
});
</script>

<style scoped>
/* Add any specific styles if needed, e.g., for smooth height transitions if card heights vary */
.md\:sticky {
  /* Ensure sticky positioning works as expected */
  align-self: flex-start;
}
</style>
